import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

const VALID_REACTIONS = new Set([
  "love",
  "jealous",
  "fire",
]);

export default function useReactions(
  targetType,
  targetId,
  currentUser,
  ownerId,
  options = {}
) {
  const { onChange } = options;

  const [counts, setCounts] = useState({
    love: 0,
    jealous: 0,
    fire: 0,
  });

  const [myReaction, setMyReaction] = useState(null);
  const [myInteractionId, setMyInteractionId] =
    useState(null);

  const currentUserId =
    currentUser?.id ||
    currentUser?.user?.id ||
    null;

  const loadReactions = useCallback(async () => {
    if (!targetId || !targetType) return;

    const { data, error } = await supabase
      .from("social_interactions")
      .select("id, user_id, interaction_type")
      .eq("target_type", targetType)
      .eq("target_id", targetId);

    if (error) {
      console.error(
        "Failed to load reactions:",
        error
      );
      return;
    }

    const totals = {
      love: 0,
      jealous: 0,
      fire: 0,
    };

    let mine = null;
    let mineId = null;

    (data || []).forEach((row) => {
      if (
        Object.prototype.hasOwnProperty.call(
          totals,
          row.interaction_type
        )
      ) {
        totals[row.interaction_type] += 1;
      }

      if (
        currentUserId &&
        row.user_id === currentUserId
      ) {
        mine = row.interaction_type;
        mineId = row.id;
      }
    });

    setCounts(totals);
    setMyReaction(mine);
    setMyInteractionId(mineId);
  }, [
    targetId,
    targetType,
    currentUserId,
  ]);

  useEffect(() => {
    loadReactions();
  }, [loadReactions]);

  const notifyChanged = useCallback(
    (details) => {
      if (typeof onChange === "function") {
        onChange({
          targetType,
          targetId,
          ...details,
        });
      }
    },
    [
      onChange,
      targetType,
      targetId,
    ]
  );

  const react = useCallback(
    async (reaction) => {
      if (
        !currentUserId ||
        !targetId ||
        !targetType ||
        !VALID_REACTIONS.has(reaction)
      ) {
        return;
      }

      /*
       * Remove the current reaction.
       *
       * The database trigger now removes the corresponding
       * notification automatically.
       */
      if (myReaction === reaction) {
        let query = supabase
          .from("social_interactions")
          .delete();

        query = myInteractionId
          ? query.eq(
              "id",
              myInteractionId
            )
          : query
              .eq(
                "target_type",
                targetType
              )
              .eq(
                "target_id",
                targetId
              )
              .eq(
                "user_id",
                currentUserId
              );

        const { error } = await query;

        if (error) {
          console.error(
            "Failed to remove reaction:",
            error
          );
          return;
        }

        setCounts((prev) => ({
          ...prev,
          [reaction]: Math.max(
            (prev[reaction] || 0) - 1,
            0
          ),
        }));

        setMyReaction(null);
        setMyInteractionId(null);

        notifyChanged({
          action: "removed",
          reaction,
        });

        return;
      }

      /*
       * Change an existing reaction.
       *
       * The database trigger now updates the corresponding
       * notification automatically.
       */
      if (myReaction) {
        let query = supabase
          .from("social_interactions")
          .update({
            interaction_type: reaction,
          });

        query = myInteractionId
          ? query.eq(
              "id",
              myInteractionId
            )
          : query
              .eq(
                "target_type",
                targetType
              )
              .eq(
                "target_id",
                targetId
              )
              .eq(
                "user_id",
                currentUserId
              );

        const {
          data,
          error,
        } = await query
          .select("id")
          .single();

        if (error) {
          console.error(
            "Failed to update reaction:",
            error
          );
          return;
        }

        const previousReaction =
          myReaction;

        const interactionId =
          data?.id ||
          myInteractionId;

        setCounts((prev) => ({
          ...prev,
          [previousReaction]: Math.max(
            (prev[previousReaction] || 0) - 1,
            0
          ),
          [reaction]:
            (prev[reaction] || 0) + 1,
        }));

        setMyReaction(reaction);

        if (interactionId) {
          setMyInteractionId(
            interactionId
          );
        }

        notifyChanged({
          action: "changed",
          reaction,
          previousReaction,
        });

        return;
      }

      /*
       * Add a new reaction.
       *
       * The database trigger now creates the corresponding
       * notification automatically.
       */
      const {
        data,
        error,
      } = await supabase
        .from("social_interactions")
        .insert({
          target_type: targetType,
          target_id: targetId,
          user_id: currentUserId,
          interaction_type: reaction,
        })
        .select("id")
        .single();

      if (error) {
        console.error(
          "Failed to add reaction:",
          error
        );
        return;
      }

      setCounts((prev) => ({
        ...prev,
        [reaction]:
          (prev[reaction] || 0) + 1,
      }));

      setMyReaction(reaction);
      setMyInteractionId(data.id);

      notifyChanged({
        action: "added",
        reaction,
      });
    },
    [
      currentUserId,
      targetId,
      targetType,
      myReaction,
      myInteractionId,
      notifyChanged,
    ]
  );

  return {
    counts,
    myReaction,
    react,
    refreshReactions: loadReactions,
  };
}

